import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  Paper,
  Typography,
  Container,
  CircularProgress,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Loading } from "../../../components/Loading";
import NavigationHeader from "../../../components/NavigationHeader";
import { getValidToken } from "../../../utils/authUtils";
import { useToast } from "../../../components/Toast";
import AddPickupManModal from "./AddPickupManModal";
import { tableHeaderStyles } from "../../../utils/styles";
import { tableCellStyles } from "../../../utils/styles";

export interface PickupMan {
  id?: string;
  companyId: string;
  name: string;
  contactNumber: string;
  commissionType: string | null;
  commissionValue: number | null;
  location: string | null;
}

const PickupMan = () => {
  const [pickupMen, setPickupMen] = useState<PickupMan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPickupMan, setSelectedPickupMan] = useState<
    PickupMan | undefined
  >();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showToast } = useToast();

  useEffect(() => {
    loadPickupMen();
  }, [page]);

  const loadPickupMen = async () => {
    setIsLoading(true);
    try {
      const token = await getValidToken();
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });
      const url = `${process.env.REACT_APP_API_HOST}master-data/pickup-man?${queryParams}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        setPickupMen(response.data?.data);
        setTotalPages(response.data?.pagination?.pages || 1);
      } else {
        setPickupMen([]);
      }
    } catch (error) {
      showToast("Error fetching pickup man data", "error");
      setPickupMen([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPickupMan = () => {
    setSelectedPickupMan(undefined);
    setIsModalOpen(true);
  };

  const handleEditPickupMan = (pickupMan: PickupMan) => {
    setSelectedPickupMan(pickupMan);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPickupMan(undefined);
  };

  const handleSubmit = async (pickupManData: any) => {
    setIsSubmitting(true);
    const token = await getValidToken();
    if (selectedPickupMan) {
      // Update existing pickup man
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_HOST}master-data/pickup-man/${pickupManData?.id}`,
          pickupManData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Pickup man updated successfully", "success");
          loadPickupMen();
        } else {
          showToast(`Error updating pickup man`, "error");
        }
      } catch (error) {
        console.error("Error updating pickup man", error);
        showToast(`Error updating pickup man`, "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new pickup man
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}master-data/pickup-man`,
          pickupManData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Pickup man added successfully", "success");
          loadPickupMen();
        } else {
          showToast(`Error submitting data`, "error");
        }
      } catch (error) {
        console.error("Error adding pickup man", error);
        showToast(`Error adding pickup man`, "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={false} />
    ) : (
      <Loading message="Loading pickup man..." fullScreen={false} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Pickup Man" },
        ]}
        title="Pickup Man"
        button={{
          label: "Add Pickup Man",
          onClick: handleAddPickupMan,
          icon: <AddIcon />,
        }}
      />
      

      {pickupMen.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No pickup man found
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="Pickup man table">
              <TableHead>
                <TableRow>
                  <TableCell {...tableHeaderStyles}>Name</TableCell>
                  <TableCell {...tableHeaderStyles}>Contact Number</TableCell>
                  <TableCell {...tableHeaderStyles}>Commission Type</TableCell>
                  <TableCell {...tableHeaderStyles}>Commission Value</TableCell>
                  <TableCell {...tableHeaderStyles}>Location</TableCell>
                  <TableCell {...tableHeaderStyles} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pickupMen.map((pickupMan) => (
                  <TableRow
                    key={pickupMan.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <TableCell {...tableCellStyles}>{pickupMan.name}</TableCell>
                    <TableCell {...tableCellStyles}>{pickupMan.contactNumber}</TableCell>
                    <TableCell {...tableCellStyles}>{pickupMan.commissionType || "-"}</TableCell>
                    <TableCell {...tableCellStyles}>
                      <Typography variant="body2">
                        {pickupMan.commissionValue
                          ? `${
                              pickupMan.commissionType === "Percentage"
                                ? ""
                                : "₹"
                            }${pickupMan.commissionValue}${
                              pickupMan.commissionType === "Percentage"
                                ? "%"
                                : ""
                            }`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell {...tableCellStyles}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pickupMan.location || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell {...tableCellStyles} align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditPickupMan(pickupMan)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <AddPickupManModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        pickupMan={selectedPickupMan}
        isEdit={!!selectedPickupMan}
      />
    </Container>
  );
};

export default PickupMan;
